/**
 *
 * Paypal
 *
 */

import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  Popup,
  Space,
  Conditional,
} from '@cashfree-intl/coherent';

import EVENTS from '../../../constants/analytics';
import Analytics from '../../../utils/analytics';

import CustomIcons from '../../../components/CustomIcons';
import { PAYPAL, PAYPAL_CONFIG, RESET_PAYPAL_MODAL } from '../constants';

function Paypal(props) {
  const {
    connectAccount,
    data,
    loading,
    enableLoader,
    activatePaypalCurrency,
    deactivatePaypalCurrency,
    updateDefaultPaypalCurrency,
    openConfirmModal,
  } = props;

  const [editKey, resetEditKey] = useState([]);

  const [selectedDefaultCurrency, resetDefaultCurrency] = useState('');
  const [selectedActiveCurrency, resetActiveCurrency] = useState('');
  const [ctaLoader, resetCtaLoader] = useState(false);

  const showDetailedView = data && !!data.paypalAccount;

  const accountStatus =
    showDetailedView && data.paypalAccount.active ? 'ACTIVE' : '';

  const handleEditCTAClick = key => () => {
    resetEditKey([...editKey, key]);
  };

  const onDefaultCurrencyChange = (e, { value }) => resetDefaultCurrency(value);

  const onActiveCurrencyChange = (e, { value }) => resetActiveCurrency(value);

  const handleConfirmClick = key => () => {
    const obj = {
      successCb: () => {
        resetCtaLoader('');
        const arr = editKey.filter(elem => elem !== key);
        resetEditKey(arr);
        if (key === 'defaultPaypalCurrency') resetDefaultCurrency('');
        else resetActiveCurrency('');
      },
      errorCb: () => resetCtaLoader(''),
    };

    if (key === 'defaultPaypalCurrency') {
      resetCtaLoader(key);
      updateDefaultPaypalCurrency({
        ...obj,
        currencyCode: selectedDefaultCurrency || data.defaultPaypalCurrency,
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.DEFAULT_CURRENCY, {
        currencyCode: selectedDefaultCurrency || data.defaultPaypalCurrency,
      });
    } else if (selectedActiveCurrency) {
      resetCtaLoader(key);
      activatePaypalCurrency({ ...obj, currencyCode: selectedActiveCurrency });
      Analytics.track(EVENTS.PAYMENT_METHODS.ACTIVE_CURRENCY, {
        currencyCode: [...data.activePaypalCurrencies, selectedActiveCurrency],
      });
    } else {
      Analytics.track(EVENTS.PAYMENT_METHODS.ACTIVE_CURRENCY, {
        currencyCode: data.activePaypalCurrencies,
      });
    }
  };

  const deactivateCurrency = currency => () => {
    enableLoader();
    deactivatePaypalCurrency({ currencyCode: currency });
  };

  const getDropdownOptions = type =>
    data.paypalCurrencies.filter(obj => {
      if (type === 'defaultPaypalCurrency') return true;
      return !data.activePaypalCurrencies.includes(obj.value);
    });

  const renderAccountDetails = () => {
    const lookupObj = {
      ...data.paypalAccount,
      activePaypalCurrencies:
        data.activePaypalCurrencies && data.activePaypalCurrencies.join(', '),
      defaultPaypalCurrency: data.defaultPaypalCurrency,
    };

    return PAYPAL_CONFIG.map(obj => (
      <div className="details-row flex" key={obj.key}>
        <div className="detail-name flex">
          {obj.displayText}
          {obj.showInfoIcon && (
            <Popup
              key={obj.key}
              content={`Popup content for ${obj.displayText}`}
              position="right center"
              trigger={
                <span className="icon pointer">
                  <CustomIcons componentName="INFO" />
                </span>
              }
            />
          )}
        </div>
        <div className="detail-value flex">
          {editKey.includes(obj.key) ? (
            <>
              {obj.key === 'defaultPaypalCurrency' && (
                <div className="flex">
                  <Dropdown
                    options={getDropdownOptions(obj.key)}
                    selection
                    value={
                      selectedDefaultCurrency || data.defaultPaypalCurrency
                    }
                    onChange={onDefaultCurrencyChange}
                    icon={<CustomIcons componentName="CHEVRONDOWN" />}
                  />
                  <Button
                    secondary
                    onClick={handleConfirmClick(obj.key)}
                    disabled={ctaLoader === obj.key}
                    loading={ctaLoader === obj.key}
                  >
                    Confirm
                  </Button>
                </div>
              )}
              {obj.key === 'activePaypalCurrencies' && (
                <div className>
                  <div className="active-currency-label-wrapper flex">
                    {data.activePaypalCurrencies.map(currency => (
                      <div
                        key={currency}
                        className="active-currency-label flex"
                      >
                        {currency}
                        {/* eslint-disable-next-line */}
                        <div
                          className="deactivate-cta"
                          onClick={deactivateCurrency(currency)}
                        >
                          <CustomIcons componentName="CLOSE_SMALL" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex">
                    <Dropdown
                      options={getDropdownOptions(obj.key)}
                      selection
                      value={selectedActiveCurrency}
                      onChange={onActiveCurrencyChange}
                      placeholder="Add currency"
                      icon={<CustomIcons componentName="CHEVRONDOWN" />}
                    />
                    <Button
                      secondary
                      onClick={handleConfirmClick(obj.key)}
                      disabled={ctaLoader === obj.key}
                      loading={ctaLoader === obj.key}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {obj.isAmount && typeof lookupObj[obj.key] === 'number'
                ? `₹ ${lookupObj[obj.key]}`
                : lookupObj[obj.key]}
              {editKey !== obj.key && obj.isEditable && (
                // eslint-disable-next-line
                <span
                  className="edit-cta pointer"
                  onClick={handleEditCTAClick(obj.key)}
                >
                  <CustomIcons componentName="EDIT" />
                </span>
              )}
            </>
          )}
        </div>
      </div>
    ));
  };

  return showDetailedView && accountStatus ? (
    <div className="third-party-payment-method-wrapper">
      <div className="third-party-payment-method active">
        <Space justifyContent="space-between" alignItems="center">
          <Space className="payment-method-card-header">
            <CustomIcons componentName={PAYPAL} />
            <Conditional if={!!accountStatus}>
              <div className={`status ${accountStatus.toLowerCase()}`}>
                {accountStatus}
              </div>
            </Conditional>
          </Space>
          <Button
            secondary
            size="small"
            className="reset-cta"
            onClick={() => openConfirmModal(RESET_PAYPAL_MODAL)}
            loading={loading}
            disabled={loading}
          >
            Deactivate
          </Button>
        </Space>
        <div className="payment-method-card-content">
          {renderAccountDetails()}
        </div>
      </div>
    </div>
  ) : (
    <div className="third-party-payment-method-wrapper">
      <Space
        className="third-party-payment-method"
        direction="column"
        key={PAYPAL}
      >
        <Space>
          <CustomIcons componentName={PAYPAL} />
        </Space>
        <Space justifyContent="space-between" alignItems="center">
          <div className="description">
            Connect your PayPal account with Cashfree to enable this payment
            method.
          </div>
          <Button
            secondary
            size="small"
            className="cta-wrapper"
            onClick={connectAccount}
            loading={loading}
            disabled={loading}
          >
            Connect Account
          </Button>
        </Space>
      </Space>
    </div>
  );
}

Paypal.propTypes = {
  connectAccount: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  activatePaypalCurrency: PropTypes.func.isRequired,
  deactivatePaypalCurrency: PropTypes.func.isRequired,
  updateDefaultPaypalCurrency: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

export default memo(Paypal);
